import { ReactNode, useEffect, useState } from "react"

import DataContext from "./DataContext"
import { IAppData } from "../../AppDataType"
import { HandleLanguageChange, LanguageOptions } from "../../types";
import esData from "../../assets/data/esData.json";
import enData from "../../assets/data/enData.json";
import ptData from "../../assets/data/ptData.json";
import Spinner from "../../components/Spinner/Spinner";
import mapRoute from "../../assets/scripts/mapRoute";

interface Props {
    children: ReactNode;
    lang?: LanguageOptions
}

const DataProvider: React.FC<Props> = ({ children, lang }) => {
    const [data, setData] = useState<IAppData>(() => {
        if (lang) {
            if (lang === "en") return enData;
            if (lang === "pt") return ptData;
            return esData;
        }
        return esData
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentLang, setCurrentLang] = useState<string>("es");

    useEffect(() => {
        let storedLang = undefined;
        if (typeof window !== "undefined") {
            storedLang = localStorage.getItem("lang");
        }
        const initialLang = storedLang ?? lang ?? "es";

        if (initialLang !== currentLang) {
            setLanguage(initialLang as LanguageOptions).catch((err: Error) => console.error(err.message));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    const setLanguage: HandleLanguageChange = async (lang, currentPathname, navigate) => {
        // Evitar recargar el mismo idioma.
        if (lang === currentLang) return;

        // Cambio de idioma.
        setIsLoading(true);

        try {
            switch (lang) {
                case "en":
                    await Promise.resolve(); //=> Es necesario un pequeño retraso para la actualización. 
                    setData(enData);
                    break;

                case "pt":
                    await Promise.resolve(); //=> Es necesario un pequeño retraso para la actualización.
                    setData(ptData);
                    break;

                default:
                    await Promise.resolve(); //=> Es necesario un pequeño retraso para la actualización.
                    setData(esData);
                    break;
            }

            setCurrentLang(lang);
            
            if (typeof window !== "undefined") {
                localStorage.setItem("lang", lang);
            }

            document.documentElement.lang = lang;

            // Luego de la actualización de idioma, se navega a la ruta correspondiente 
            // en el otro idioma para evitar volver al inicio.
            if (currentPathname && navigate) {
                const newRoute = mapRoute(currentPathname, lang);
                navigate(newRoute);
            }
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setIsLoading(false);
        }
    }

    return (
        <DataContext.Provider value={{ data, setLanguage, currentLang }}>
            { isLoading ? <Spinner color="bg-first-solid" pxSize={20} height="screen"/> : children }
        </DataContext.Provider>
    );
}

export default DataProvider;
